// The pop-out camera matrix (#/matrix/:eventId, design/README.md agreed 2026-10-16): every camera at the event's site
// as a grid of live feeds, in its own browser window (for a second monitor), without the app chrome.
//   order   alarm cameras (newest alarm first), cameras the agent cited, map neighbours by distance, the rest
//           (cameraRank.ts); each tile says why it is there. New alarms reorder it, but not while the pointer is on
//           the grid.
//   time    follows the console's player for this event over a BroadcastChannel (matrixLink.ts): live or the same
//           archive time; Live / Sync to alarm / ±10 s / pause here drive the console's player. Clicking a tile loads
//           that camera in the console's main canvas. Without a console on this event it keeps its own clock.
//   watch   every tile is a CameraFeed, so its time counts like any other player (useWatchTracker); tiles scrolled out
//           of view close their session, so only tiles on screen and playing count.
// Any role that can open the event can open the matrix; a closed event shows a notice, a bad id an error.

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type RefObject } from 'react';
import { api, ApiError, type AgentPanel, type EventDetail } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { CameraFeed } from '../components/VideoPlayer.tsx';
import { ErrorBox, Loading } from '../components/ui.tsx';
import { useLoad, useNow } from '../hooks.ts';
import { useSession } from '../session.tsx';
import { useStreamEvent } from '../stream.tsx';
import { fmtDateTime, fmtTime, tzAbbr } from '../time.ts';
import { stateLabel } from './ai.ts';
import { rankCameras, type RankedCam } from './cameraRank.ts';
import { HEARTBEAT_MS, openChannel, type MatrixCmd, type MatrixMsg } from './matrixLink.ts';
import { alarmMs, isTyping, normalizeEvent, plural, SEV_CLS } from './util.ts';
import { goLive, initialView, PRE_ROLL_MS, seek, selectedAlarm, toIso, togglePause, vtime, type View } from './view.ts';

const GAP = 6;
const MIN_TILE = 240;

/** Columns and tile width that fit n 16:9 tiles in w × h (or the widest that still scrolls). */
function fitGrid(n: number, w: number, h: number): { cols: number; tileW: number } {
  if (n <= 0 || w <= 0) return { cols: 1, tileW: Math.max(MIN_TILE, w) };
  let best = { cols: 1, tileW: 0 };
  for (let c = 1; c <= n; c++) {
    const r = Math.ceil(n / c);
    const tw = Math.min((w - (c - 1) * GAP) / c, ((h - (r - 1) * GAP) / r) * (16 / 9));
    if (tw > best.tileW) best = { cols: c, tileW: tw };
  }
  if (best.tileW >= MIN_TILE) return { cols: best.cols, tileW: Math.floor(best.tileW) };
  const cols = Math.max(1, Math.floor((w + GAP) / (MIN_TILE + GAP)));
  return { cols, tileW: Math.floor((w - (cols - 1) * GAP) / cols) };
}

/** Mounts children only while the element is on screen (with a little hysteresis so scrolling does not churn). */
function useOnScreen<T extends Element>(): [RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setOn(true);
      return;
    }
    let t: ReturnType<typeof setTimeout> | null = null;
    const io = new IntersectionObserver((entries) => {
      const vis = entries.some((e) => e.isIntersecting);
      if (t) clearTimeout(t);
      t = setTimeout(() => setOn(vis), vis ? 150 : 1200);
    });
    io.observe(el);
    return () => {
      if (t) clearTimeout(t);
      io.disconnect();
    };
  }, []);
  return [ref, on];
}

function Tile({
  r,
  keyNo,
  at,
  paused,
  badge,
  isMain,
  started,
  eventId,
  nonce,
  epoch,
  onPick,
}: {
  r: RankedCam;
  keyNo: number | null;
  at: string | null;
  paused: boolean;
  badge: { text: string; cls: 'live' | 'archive' | 'alarm' };
  isMain: boolean;
  started: boolean;
  eventId: string | null;
  nonce: number;
  /** The view's position epoch: a tile reopens on a new position, not on a restatement of the same one. */
  epoch: number;
  onPick: (camId: string) => void;
}) {
  const [ref, onScreen] = useOnScreen<HTMLDivElement>();
  const off = r.cam.health?.status === 'offline';
  const pick = () => onPick(r.cam.id);
  return (
    <div
      ref={ref}
      className={`mtile w-${r.why.kind}${isMain ? ' main' : ''}${off ? ' off' : ''}`}
      role="button"
      tabIndex={0}
      aria-label={`${r.cam.name} · ${r.label}${isMain ? ' · in the console now' : ''}. Load it in the console.`}
      title={`${r.cam.name} · ${r.label}${off ? ' · reported offline' : ''}\nClick to load it in the console's main canvas`}
      onClick={pick}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), e.stopPropagation(), pick())}
    >
      {onScreen && started ? (
        <CameraFeed cameraId={r.cam.id} cameraName={r.cam.name} at={at} eventId={eventId} paused={paused} badge={badge} offline={off} nonce={nonce} epoch={epoch} />
      ) : (
        <div className="feed off">
          <div className="osd tl">
            <span className="cn">{r.cam.name}</span>
          </div>
          <span className="nosig info">{started ? 'Paused while off screen' : 'Waiting for the console…'}</span>
        </div>
      )}
      <span className="mfoot">
        <span className={`mwhy w-${r.why.kind}`}>
          {r.why.kind === 'alarm' && <Icon name="alert" />}
          {r.why.kind === 'ai' && <Icon name="spark" />}
          {r.why.kind === 'near' && <Icon name="map" />}
          {r.label}
        </span>
        {isMain && <span className="mmain">IN CONSOLE</span>}
        <span className="sp" />
        {keyNo !== null && (
          <span className="mkey" title={`Key ${keyNo} in the console`}>
            {keyNo}
          </span>
        )}
      </span>
    </div>
  );
}

export function Clock({ view, tz, linked }: { view: View; tz: string; linked: boolean }) {
  const now = useNow(1000);
  const t = vtime(view, now);
  const live = view.mode === 'live';
  return (
    <span className="mclock">
      <span className={`mode ${live ? 'live' : 'archive'}`}>{live ? 'LIVE' : linked ? 'SYNC' : 'ARCHIVE'}</span>
      <b className="mono">{fmtTime(t, tz)}</b>
      <span className="dim mono">{tzAbbr(tz, t)}</span>
      {view.pauseStart !== null && <span className="pill">paused</span>}
    </span>
  );
}

export function Notice({ title, body, error, onRetry }: { title: string; body?: string; error?: unknown; onRetry?: () => void }) {
  return (
    <div className="matrix">
      <div className="mempty">
        {error ? (
          <ErrorBox error={error} title={title} onRetry={onRetry} />
        ) : (
          <div className="errbox" role="alert">
            <Icon name="cam" />
            <div>
              <b>{title}</b>
              {body && <div>{body}</div>}
            </div>
          </div>
        )}
        <button type="button" className="btn" onClick={() => window.close()}>
          Close this window
        </button>
      </div>
    </div>
  );
}

export function MatrixScreen({ eventId }: { eventId: string | null }) {
  const s = useSession();
  const agentOn = s.config.features.agent;
  const evLoad = useLoad<EventDetail | null>((sig) => (eventId ? api.event(eventId, sig).then(normalizeEvent) : Promise.resolve(null)), [eventId]);
  const agentLoad = useLoad<AgentPanel | null>(
    (sig) => (agentOn && eventId ? api.agentPanel(eventId, sig).catch((e: unknown) => (e instanceof ApiError && (e.status === 404 || e.status === 403) ? null : Promise.reject(e))) : Promise.resolve(null)),
    [agentOn, eventId],
  );
  const ev = evLoad.data && evLoad.data.id === eventId ? evLoad.data : null;
  const reloadEv = evLoad.reload;
  const reloadAgent = agentLoad.reload;

  // Live updates: the event (new alarms attach, it closes) and the agent (new evidence, subjects).
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const soon = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      reloadEv();
      reloadAgent();
    }, 300);
  }, [reloadEv, reloadAgent]);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);
  useStreamEvent('event', (sum) => {
    if (sum.id === eventId) soon();
  });
  useStreamEvent('agent', (m) => {
    if (m?.eventId && m.eventId === eventId) soon();
  });

  /* ---------- the console link ---------- */
  const [view, setView] = useState<View | null>(null);
  const [linkedAt, setLinkedAt] = useState<number | null>(null);
  const [started, setStarted] = useState(false);
  const versionRef = useRef<number | null>(null);
  versionRef.current = ev?.version ?? null;
  const ch = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    if (!eventId) return;
    const c = openChannel();
    ch.current = c;
    if (!c) {
      setStarted(true);
      return;
    }
    c.onmessage = (e: MessageEvent<MatrixMsg>) => {
      const m = e.data;
      if (!m || typeof m !== 'object' || m.eventId !== eventId) return;
      if (m.t === 'view') {
        setView({ ...m.view, suspended: false });
        setLinkedAt(Date.now());
        setStarted(true);
      } else if (m.t === 'bye') setLinkedAt(null);
      else if (m.t === 'event' && (versionRef.current === null || m.version > versionRef.current)) soon();
    };
    c.postMessage({ t: 'hello', eventId } satisfies MatrixMsg);
    // No console on this event: start on our own clock.
    const t = setTimeout(() => setStarted(true), 700);
    return () => {
      clearTimeout(t);
      c.close();
      ch.current = null;
    };
  }, [eventId, soon]);
  const now = useNow(1000);
  const linked = linkedAt !== null && now - linkedAt < HEARTBEAT_MS * 2.5;

  // Our own clock until the console speaks: the first alarm, like the console's first view.
  useEffect(() => {
    if (!ev || view) return;
    const v = initialView(ev);
    setView({ ...v, mode: v.seekAt === null ? 'live' : 'archive' });
  }, [ev, view]);

  const send = (cmd: MatrixCmd) => {
    if (!eventId) return;
    try {
      ch.current?.postMessage({ t: 'cmd', eventId, ...cmd } satisfies MatrixMsg);
    } catch {
      /* closed */
    }
  };
  /** Linked: the console runs it and answers with its new view. Alone: run it here. */
  const act = (cmd: MatrixCmd) => {
    if (linked) {
      send(cmd);
      return;
    }
    if (!ev) return;
    const t = Date.now();
    setView((v) => {
      if (!v) return v;
      switch (cmd.cmd) {
        case 'live':
          return goLive(v, t);
        case 'sync': {
          const a = selectedAlarm(ev, v);
          return a ? { ...seek(v, alarmMs(a) - PRE_ROLL_MS, t, 'archive'), synced: true } : v;
        }
        case 'seek':
          return seek(v, cmd.at, t, 'archive');
        case 'pause':
          return (v.pauseStart !== null) === cmd.paused ? v : togglePause(v, t);
        case 'camera':
          return { ...v, cameraId: cmd.cameraId };
      }
    });
  };
  const actRef = useRef(act);
  actRef.current = act;
  const viewRef = useRef(view);
  viewRef.current = view;

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented || isTyping(e.target)) return;
      const k = e.key.toLowerCase();
      const v = viewRef.current;
      if (k === 'l') actRef.current({ cmd: 'live' });
      else if (k === 's') actRef.current({ cmd: 'sync' });
      else if (k === ' ' && v) {
        e.preventDefault();
        actRef.current({ cmd: 'pause', paused: v.pauseStart === null });
      }
    };
    document.addEventListener('keydown', on);
    return () => document.removeEventListener('keydown', on);
  }, []);

  /* ---------- order: relevance, frozen while the pointer is on the grid ---------- */
  const ranked = useMemo(() => (ev ? rankCameras(ev, agentLoad.data) : []), [ev, agentLoad.data]);
  const [hover, setHover] = useState(false);
  const [order, setOrder] = useState<string[]>([]);
  useEffect(() => {
    const ids = ranked.map((r) => r.cam.id);
    setOrder((prev) => {
      if (!hover || !prev.length) return prev.join() === ids.join() ? prev : ids;
      const kept = prev.filter((id) => ids.includes(id));
      const next = [...kept, ...ids.filter((id) => !kept.includes(id))];
      return next.join() === prev.join() ? prev : next;
    });
  }, [ranked, hover]);
  const tiles = order.map((id) => ranked.find((r) => r.cam.id === id)).filter((r): r is RankedCam => !!r);
  // Keys 1–9 in the console pick cameras by distance rank.
  const keyOf = useMemo(() => {
    const m = new Map<string, number>();
    (ev?.cameras ?? [])
      .slice()
      .sort((a, b) => a.distanceRank - b.distanceRank)
      .slice(0, 9)
      .forEach((c, i) => m.set(c.id, i + 1));
    return m;
  }, [ev]);

  /* ---------- grid size ---------- */
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = gridRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setBox({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, [ev?.id, view === null]);
  const fit = fitGrid(tiles.length, box.w - 2, box.h - 2);

  useEffect(() => {
    document.title = ev ? `Cameras · ${ev.site.name} · ${ev.displayNo} · EyeQ SOC` : 'Camera matrix · EyeQ SOC';
  }, [ev]);

  if (!eventId) return <Notice title="No event" body="Open the camera matrix from an event in the console (Cameras, or M)." />;
  if (evLoad.error && !ev) {
    const st = evLoad.error instanceof ApiError ? evLoad.error.status : 0;
    if (st === 404) return <Notice title="Event not found" body={`There is no event ${eventId}. It may have been merged or the link is wrong.`} />;
    if (st === 403) return <Notice title="Not available for your role" body="You cannot open this event, so you cannot see its cameras." />;
    return <Notice title="Could not load the event" error={evLoad.error} onRetry={reloadEv} />;
  }
  if (!ev || !view) {
    return (
      <div className="matrix">
        <div className="mempty">
          <Loading label="Loading cameras…" />
        </div>
      </div>
    );
  }

  const tz = ev.siteTimeZone;
  const closed = ev.state === 'closed' || ev.state === 'dispositioned';
  const canAct = ev.state === 'active' && ev.owner?.id === s.me.userId;
  const at = toIso(view.seekAt);
  const paused = view.pauseStart !== null;
  const badge = view.mode === 'live' ? ({ text: 'LIVE', cls: 'live' } as const) : ({ text: linked ? 'SYNC' : 'ARCHIVE', cls: 'archive' } as const);
  const unrev = ev.alarms.filter((a) => a.reviewStatus === 'new').length;

  return (
    <div className="matrix">
      <header className={`mbar s-${SEV_CLS[ev.severity]}`}>
        <span className="mtitle">
          <Icon name="grid" />
          Cameras
        </span>
        <span className="msite">
          <b>{ev.site.name}</b>
          <span className="mono dim">{ev.displayNo}</span>
          <span className={`pill${closed ? '' : ' warn'}`}>{stateLabel(ev.state)}</span>
          <span className="dim">
            {plural(ev.alarmCount, 'alarm')} on {plural(ev.cameraCount, 'camera')}
            {unrev > 0 && <span className="hot"> · {unrev} new</span>} · {plural(ev.cameras.length, 'site camera')}
          </span>
        </span>
        <span className="sp" />
        <span className={`mlink${linked ? ' on' : ''}`} title={linked ? 'Following the console player for this event' : 'No console is showing this event: the matrix keeps its own clock'}>
          <i aria-hidden="true" />
          {linked ? 'Following the console' : 'Not linked to the console'}
        </span>
        <Clock view={view} tz={tz} linked={linked} />
        <div className="mctl" role="group" aria-label="Matrix time">
          <button type="button" className={`btn sm${view.mode === 'live' ? ' on' : ''}`} onClick={() => act({ cmd: 'live' })} aria-pressed={view.mode === 'live'}>
            Live <kbd>L</kbd>
          </button>
          <button type="button" className={`btn sm${view.synced ? ' on' : ''}`} onClick={() => act({ cmd: 'sync' })} title="Every camera at the selected alarm's time">
            <Icon name="sync" />
            Sync to alarm <kbd>S</kbd>
          </button>
          <button type="button" className="btn sm icon" onClick={() => act({ cmd: 'seek', at: vtime(view, Date.now()) - 10_000 })} aria-label="Back 10 seconds" title="Back 10 s">
            <Icon name="rew" />
          </button>
          <button type="button" className="btn sm icon" onClick={() => act({ cmd: 'pause', paused: !paused })} aria-label={paused ? 'Play' : 'Pause'} title="Play / pause (Space)">
            <Icon name={paused ? 'play' : 'pause'} />
          </button>
          <button type="button" className="btn sm icon" onClick={() => act({ cmd: 'seek', at: vtime(view, Date.now()) + 10_000 })} disabled={view.mode === 'live'} aria-label="Forward 10 seconds" title="Forward 10 s">
            <Icon name="fwd" />
          </button>
        </div>
      </header>
      {closed && (
        <div className="robar mnotice" role="status">
          <Icon name="lock" />
          <span>
            This event is closed{ev.closedAt ? ` (${fmtDateTime(ev.closedAt, tz)})` : ''}. The cameras stay open for review; the console no longer follows it.
          </span>
          <span className="sp" />
          <button type="button" className="btn sm" onClick={() => window.close()}>
            Close window
          </button>
        </div>
      )}
      {!linked && !closed && <div className="note mhint">Open this event in the console to drive the matrix from its player, and to load a camera there by clicking its tile.</div>}
      <div
        ref={gridRef}
        className="mgrid-wrap"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onKeyDown={(e: ReactKeyboardEvent) => e.key === 'Escape' && (e.currentTarget as HTMLElement).blur()}
      >
        {tiles.length === 0 ? (
          <div className="mempty">
            <span className="note">This site has no cameras in the replica.</span>
          </div>
        ) : (
          <div className="mgrid" style={{ gridTemplateColumns: `repeat(${fit.cols}, ${fit.tileW}px)` }}>
            {tiles.map((r) => (
              <Tile
                key={r.cam.id}
                r={r}
                keyNo={keyOf.get(r.cam.id) ?? null}
                at={at}
                paused={paused}
                badge={badge}
                isMain={linked && view.cameraId === r.cam.id}
                started={started}
                eventId={canAct ? ev.id : null}
                nonce={view.nonce}
                epoch={view.epoch ?? 0}
                onPick={(id) => (linked ? send({ cmd: 'camera', cameraId: id }) : act({ cmd: 'camera', cameraId: id }))}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
